"use client";

import React, { useEffect, useRef } from "react";
import { Renderer, Program, Mesh, Triangle } from "ogl";

const vertexShader = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0, 1);
  }
`;

const createShader = (
  baseColor: [number, number, number],
  accentColor: [number, number, number],
  mistColor: [number, number, number]
) => `
  precision highp float;

  uniform float uTime;
  uniform vec3 uResolution;
  varying vec2 vUv;

  void main() {
    float mr = min(uResolution.x, uResolution.y);
    vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

    float d = -uTime * 0.9;
    float a = 0.0;

    for (float i = 0.0; i < 10.0; ++i) {
      float wave = sin(i * 0.35) * 0.35 + 0.75;
      a += cos(i - d - a * uv.x) * wave;
      d += sin(uv.y * i + a) * wave;
    }

    d += uTime * 0.55;

    vec3 col = vec3(${baseColor.join(", ")}) +
               0.18 * vec3(
                 sin(d * 0.35) * 0.35 + 0.45,
                 cos(a * 0.45) * 0.45 + 0.55,
                 sin(a * 0.22 + d * 0.18) * 0.12 + 0.18
               );

    float greenBlend = (sin(d * 0.22) + cos(a * 0.26)) * 0.18 + 0.24;
    float mistBlend = (cos(d * 0.18) + sin(a * 0.2)) * 0.14 + 0.18;

    col = mix(col, vec3(${accentColor.join(", ")}), greenBlend);
    col = mix(col, vec3(${mistColor.join(", ")}), mistBlend);

    gl_FragColor = vec4(col, 1.0);
  }
`;

interface NovatrixProps {
  baseColor: [number, number, number];
  accentColor: [number, number, number];
  mistColor: [number, number, number];
}

const Novatrix: React.FC<NovatrixProps> = ({
  baseColor,
  accentColor,
  mistColor,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 2),
      antialias: true,
      alpha: true,
    });
    const gl = renderer.gl;

    const handleResize = () => {
      const { width, height } = container.getBoundingClientRect();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    const geometry = new Triangle(gl);

    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: createShader(baseColor, accentColor, mistColor),
      uniforms: {
        uTime: { value: 0 },
        uResolution: {
          value: [
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height,
          ],
        },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });

    let rafId: number;

    const animate = (time: number) => {
      rafId = requestAnimationFrame(animate);
      program.uniforms.uTime.value = time * 0.001;
      renderer.render({ scene: mesh });
    };

    container.appendChild(gl.canvas);
    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      if (container.contains(gl.canvas)) {
        container.removeChild(gl.canvas);
      }
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      geometry.remove();
      program.remove();
    };
  }, [baseColor, accentColor, mistColor]);

  return <div ref={containerRef} className="h-full w-full bg-transparent" />;
};

const SenuriGrandCastelloBackground: React.FC = () => {
  const baseColor: [number, number, number] = [0.03, 0.09, 0.06];
  const accentColor: [number, number, number] = [0.74, 0.86, 0.77];
  const mistColor: [number, number, number] = [0.96, 0.98, 0.96];

  return (
    <div className="absolute inset-0 h-full w-full">
      <Novatrix
        baseColor={baseColor}
        accentColor={accentColor}
        mistColor={mistColor}
      />
    </div>
  );
};

export default SenuriGrandCastelloBackground;
